import api, { esCdn, omniChannelApi } from '.';
import logger from '../utils/logger';
import { Branch } from '../types/location';
import { createNearestBranchRequestBody } from '../types/branch-config';
import { EquipmentClassList, Price } from '../types/equipment';
import { RENTAL_SERVICE_SLUG_FOR_NEAREST_BRANCH } from '../constants/locations';
import { RentalProtectionPlan, ShiftPlan } from '../types/cart-state';
import {
  BranchFinderRentalService,
  mapBranchFinderLocationToBranch,
} from '../utils/mapLocationFinderToBranch';

export const getNearestBranch = async (lat: number, long: number): Promise<Branch> => {
  try {
    const requestBody = createNearestBranchRequestBody(lat, long);

    const { data } = await omniChannelApi.post(`/location-finder`, requestBody);
    if (!data.locations) {
      throw new Error('Error in getNearestBranch finder, required data not found');
    }

    let location, market, rentalService;
    for (const currentLocation of data.locations) {
      for (const currentMarket of currentLocation?.markets || []) {
        rentalService = currentMarket?.rental_services?.find(
          (service: BranchFinderRentalService) =>
            service?.slug === RENTAL_SERVICE_SLUG_FOR_NEAREST_BRANCH,
        );

        if (rentalService) {
          location = currentLocation;
          market = currentMarket;
          break;
        }
      }

      if (rentalService) {
        break;
      }
    }

    if (!location || !market || !rentalService) {
      throw new Error('Error in getNearestBranch finder, required data not found');
    }

    const branch = mapBranchFinderLocationToBranch(location, market, rentalService);
    if (!branch) {
      throw new Error('error at getNearestBranch finder, branch data not found');
    }

    return branch;
  } catch (error) {
    logger('error', 'at getNearestBranch while fetching nearest branch', error);
    throw new Error('error at getNearestBranch while fetching nearest branch');
  }
};

export const getEquipmentClassList = async (
  categoryId: number,
  pageNumber: number,
  pageSize: number,
  filters?: string,
): Promise<EquipmentClassList> => {
  try {
    const { data } = await api.get(
      `/rental-equipment-search/categories/${categoryId}?page=${pageNumber}&per_page=${pageSize}${
        filters ?? ''
      }`,
    );
    return data.data;
  } catch (error) {
    logger('error', 'at getEquipmentClassList while fetching equipment classes', error);
    throw new Error('error at getEquipmentClassList while fetching equipment classes');
  }
};

export const getPricesForEquipmentClasses = async (
  branchId: string | number,
  equipmentIds: Array<string | number>,
): Promise<Price[]> => {
  const params = {
    branch_id: branchId,
    equipment_class_ids: equipmentIds,
  };
  try {
    const { data } = await api.post('/rental-rates/multiple', params);
    return data;
  } catch (error) {
    logger('error', 'at getAllPrices while fetching prices', error);
    throw new Error('error at getAllPrices while fetching prices');
  }
};

export const getRentalShifts = async (): Promise<ShiftPlan[]> => {
  try {
    const { data } = await esCdn.get('/shared/shifts.json');
    return data;
  } catch (error) {
    logger('error', 'at getRentalShifts while fetching shifts data', error);
    throw new Error('error at getRentalShifts while fetching shifts data');
  }
};

export const getRentalProtectionPlans = async (): Promise<RentalProtectionPlan[]> => {
  try {
    const { data } = await api.get('/rental-protection-plans');
    return data;
  } catch (error) {
    logger('error', 'at getRentalProtectionPlans while fetching standard protection plan', error);
    throw new Error('error at getRentalProtectionPlans while fetching standard protection plan');
  }
};
